import { getAddress } from "@ethersproject/address";
import { VMType } from "../enums.js";
// Checks a string starts with 0x, is 42 characters long and contains only hex characters after 0x
const startsWith0xLen42HexRegex = /^0x[0-9a-fA-F]{40}$/;
/**
 * Checks if an address is valid by checking 0x prefix, length === 42 and hex encoding.
 * @param address the unchecksummed hex address
 */
export function checkValidAddress(address) {
    if (startsWith0xLen42HexRegex.test(address)) {
        return address;
    }
    throw new Error(`${address} is not a valid address.`);
}
/**
 * Checks if an address is valid by checking 0x prefix, length === 42 and hex encoding.
 * @param address the unchecksummed hex address
 */
export function checkValidEVMAddress(address) {
    if (startsWith0xLen42HexRegex.test(address)) {
        return address.toLowerCase();
    }
    throw new Error(`${address} is not a valid address.`);
}
// Solana address validation
// Base58 character set: [1-9A-HJ-NP-Za-km-z]
// Standard Solana addresses are 32-44 characters long
const solanaRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
export function checkValidSVMAddress(address) {
    if (solanaRegex.test(address)) {
        return address;
    }
    throw new Error(`${address} is not a valid address.`);
}
/**
 * Detect the VM type of an on-chain address from its string shape, without
 * throwing. Returns {@link VMType.EVM} for a 0x-prefixed 42-char hex string,
 * {@link VMType.SVM} for a base58 Solana address, and `undefined` for
 * anything else (e.g. an internal account id). Case is never inspected or
 * altered — base58 is case-sensitive, so callers must pass the value
 * verbatim.
 */
export function getAddressVmType(value) {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim();
    if (startsWith0xLen42HexRegex.test(trimmed)) {
        return VMType.EVM;
    }
    else if (solanaRegex.test(trimmed)) {
        return VMType.SVM;
    }
    return undefined;
}
/**
 * Validates and formats an address for ETH and SOL addresses.
 * ETH addresses are 42 characters long, starting with 0x
 * SOL addresses are 32-44 characters long, containing only alphanumeric characters
 */
export function validateAndFormatAddress(address, vmType) {
    if (!address) {
        return undefined;
    }
    // Remove any whitespace
    address = address?.trim();
    if (startsWith0xLen42HexRegex.test(address)) {
        return address.toLowerCase();
    }
    else if (solanaRegex.test(address)) {
        return address;
    }
    else if (vmType && VMType.OFFCHAIN == vmType) {
        return address;
    }
    return undefined;
}
export function validateAndLowerCase(value, vmType) {
    if (!value) {
        return undefined;
    }
    // Remove any whitespace
    value = value?.trim();
    if (solanaRegex.test(value)) {
        throw new Error(`should not lowercase solana address: ${value}`);
    }
    else if (vmType && [VMType.OFFCHAIN, VMType.OFFCHAIN, VMType.OFFCHAIN].includes(vmType)) {
        throw new Error(`should not lowercase ${vmType} address: ${value}`);
    }
    return value?.toLowerCase();
}
/**
 * Normalizes a wallet address for offchain ramp (quotes/sessions).
 * - EVM: trims and applies EIP-55 checksum via getAddress
 * - SVM (Solana): trims only, no format change
 */
export function normalizeAddress(address, vmType) {
    const trimmed = address?.trim();
    if (!trimmed)
        return undefined;
    if (vmType == VMType.EVM) {
        return startsWith0xLen42HexRegex.test(trimmed) ? getAddress(trimmed) : undefined;
    }
    if (vmType == VMType.SVM) {
        return solanaRegex.test(trimmed) ? trimmed : undefined; // Solana: trim only, no checksum
    }
    return trimmed;
}
